import { FC, useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { ModalLayout, TableLayout } from "Layouts";
import { ControlledTable, Modal, Filter2 } from "Components";
import { actionArr, FilterArr, TableCol } from "./Data";
import { RouteUrls } from "Constant/RouteUrls";
import { useDispatch, useSelector } from "react-redux";
import { CommonButtonArray } from "Components/Modal/Data";
import { setAlertData, setPageFilter, setPageLimit } from "Redux/Screen/Action";
import Formate from "./Formate";
import PatientApi from "Service/Patient.api";
import { ScreenInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { Filter2DataProps } from "DataTypes/Common";
import { PendingAssignType } from "DataTypes/Services/Patient.type";
import {
  CreateFilter2Payload,
  RedoxDataToFilter,
  TransformFilterDataRedox,
} from "Utils/common";
import UI from "Constant/UI";

const PendingAssignment: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const { listPagination, listFilters }: ScreenInitialStateType = useSelector(
    (state: any) => state?.screen
  );
  const listObj = listFilters.find(el => el.id == "pending-assignment");
  const reduxLimit = (listPagination || []).find(
    el => el.id == "pending-assignment"
  )?.limit || 10;
  const [rows, setRows] = useState<PendingAssignType[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPatient, setSelectedPatient] =
    useState<PendingAssignType | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    ...UI.pageData,
    limit: reduxLimit
  });
  const [searchText, setSearchText] = useState("");
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [filterData, setFilterData] = useState<Filter2DataProps[]>(() =>
    RedoxDataToFilter(
      JSON.parse(JSON.stringify(FilterArr())),
      listObj
    )
  );
  const tableHeight = `calc(99% - ${filterHeight}px)`;

  useEffect(() => {
    handleSetData(1, pageData.limit);
  }, [filterData]);

  const handleSetData: (
    currentPage: number,
    currentLimit: number
  ) => void = async (currentPage, currentLimit) => {
    setLoading(true);
    const newFilterPayload = CreateFilter2Payload(filterData);
    const payload = {
      ...(newFilterPayload || {}),
    };
    const res = await PatientApi.PendingAssignment({
      offset: (currentPage - 1) * currentLimit,
      limit: currentLimit,
      data: payload,
    });
    let newPageData = { ...UI.pageData }
    let newRows: PendingAssignType[] = [];
    if (res?.success) {
      newRows = Formate.PendingAssignList(res?.data);
      newPageData = res.criterion;
    }
    setRows(newRows);
    setPageData(newPageData)
    dispatch(setPageLimit({
      id: "pending-assignment",
      limit: newPageData.limit
    }));
    dispatch(setPageFilter({
      id: "pending-assignment",
      filterData: TransformFilterDataRedox(filterData)
    }));
    setLoading(false);
  };

  const handlePatientDetails: (data: PendingAssignType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}/${RouteUrls.details}`
    );
  };

  const handleChart: (data: PendingAssignType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}` +
      `/${RouteUrls.clinicalSummary}`
    );
  };

  const handleTask: (data: PendingAssignType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}` +
      `/${RouteUrls.task}/${RouteUrls.add}`
    );
  };

  const handleAssign: (data: PendingAssignType) => void = (data) => {
    setSelectedPatient(data);
    setOpenModal(true);
  };

  const handleClose = (): void => {
    setOpenModal(false);
    setSelectedPatient(null);
  };

  const handleSave: () => void = async () => {
    const cmpayload = {
      caremanagerId: myInfo.id,
    };
    const provpayload = {
      billableProviderId: myInfo.id,
    };
    const res = await PatientApi.AssignToMe({
      id1: selectedPatient && selectedPatient.enrolledProgramId,
      data:
        myInfo.roleId == 5 ? provpayload : myInfo.roleId == 3 ? cmpayload : {},
    });
    if (res.success) {
      dispatch(setAlertData(res.alert));
      setOpenModal(false);
      navigate(`../${RouteUrls.myPatients}`);
    }
  };

  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => TableCol}
      tableRow={rows}
      tableAction={(data: PendingAssignType) =>
        actionArr(
          () => handleChart(data),
          () => handlePatientDetails(data),
          () => handleTask(data),
          () => handleAssign(data),
          myInfo.roleId
        )
      }
      searchText={searchText}
    />
  );

  const handleSwitch: (prgm: string) => string = (prgm) => {
    let plan = "";
    switch (prgm) {
      case "CCM":
      case "PCM":
      case "GCM":
        plan = " to complete a Care Plan";
        break;
      case "BHI":
      case "RPM":
        plan = " to complete a Treatment Plan";
        break;
      case "TCM":
        plan = "";
        break;
      default:
        plan = "Care Plan";
    }
    return plan;
  };

  return (
    <Fragment>
      <ModalLayout
        modwidth="33%"
        heading={"Assign Patient"}
        content={
          <Modal.AssignPatient
            content={`${selectedPatient?.name
            } is being assigned to you${handleSwitch(
              selectedPatient?.program || ""
            )} to initiate ${selectedPatient?.program} services.`}
          />
        }
        open={openModal}
        onClose={() => setOpenModal(false)}
        buttonArr={CommonButtonArray(handleSave, handleClose, "Yes", "No")}
      />
      <Filter2
        filterDataArr={filterData}
        handleFilterClick={(data) => setFilterData(data)}
        changeSearchText={(val) => setSearchText(val)}
        setFilterHeight={(val) => setFilterHeight(val)}
      />
      <TableLayout
        tblBody={TblBodyJSX}
        tblHead={ControlledTable.TblHead(TableCol)}
        height={tableHeight}
        loading={loading}
        pageRecordCount={rows.length}
        totalRecordCount={pageData.total}
        Pagination={
          <ControlledTable.Pagination
            mainPage={pageData.page}
            limit={pageData.limit}
            total={pageData.total}
            handleClick={handleSetData}
            tableStyle={true}
          />
        }
      />
    </Fragment>
  );
};
export default PendingAssignment;
